import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from 'react';
import styles from './TermsOfUseContent.module.css';
export function TermsOfUseContent({ sourceUri }) {
    const iframeRef = useRef(null);
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const injectScript = `
    (function() {
      try {
        var css = 'body, html, * { background-color: #FAFAFA !important; }';
        var style = document.createElement('style');
        style.id = 'terms-of-use-background-inject';
        if (style.styleSheet) {
          style.styleSheet.cssText = css;
        } else {
          style.appendChild(document.createTextNode(css));
        }
        if (!document.getElementById('terms-of-use-background-inject')) {
          document.head.appendChild(style);
        }
        
        // Also try to set it directly on body
        if (document.body) {
          document.body.style.backgroundColor = '#FAFAFA';
          document.body.style.background = '#FAFAFA';
        }
        
        // Set on html element too
        if (document.documentElement) {
          document.documentElement.style.backgroundColor = '#FAFAFA';
          document.documentElement.style.background = '#FAFAFA';
        }
      } catch(e) {
        console.warn('Could not inject background style');
      }
    })();
  `;
    const injectStyleElement = useCallback((iframeDocument) => {
        let styleElement = iframeDocument.getElementById('terms-of-use-background-inject');
        if (!styleElement) {
            styleElement = iframeDocument.createElement('style');
            styleElement.id = 'terms-of-use-background-inject';
            const css = 'body, html, * { background-color: #FAFAFA !important; }';
            const styleSheet = styleElement.styleSheet;
            if (styleSheet) {
                styleSheet.cssText = css;
            }
            else {
                styleElement.appendChild(iframeDocument.createTextNode(css));
            }
            if (iframeDocument?.head) {
                iframeDocument.head.appendChild(styleElement);
            }
            else if (iframeDocument?.body) {
                iframeDocument.body.insertBefore(styleElement, iframeDocument.body.firstChild);
            }
        }
    }, []);
    const setBackgroundOnElements = useCallback((iframeDocument) => {
        if (iframeDocument.body) {
            iframeDocument.body.style.backgroundColor = '#FAFAFA';
        }
        if (iframeDocument.documentElement) {
            iframeDocument.documentElement.style.backgroundColor = '#FAFAFA';
        }
    }, []);
    const tryFallbackScript = useCallback((iframe) => {
        try {
            if (iframe.contentWindow) {
                new Function(injectScript).call(iframe.contentWindow);
            }
        }
        catch (evalError) {
        }
    }, [injectScript]);
    const applyBackgroundToIframe = useCallback((iframe) => {
        try {
            let iframeDocument = null;
            try {
                iframeDocument = iframe.contentDocument || (iframe.contentWindow?.document ?? null);
            }
            catch (e) {
                return;
            }
            if (!iframeDocument) {
                return;
            }
            try {
                injectStyleElement(iframeDocument);
                setBackgroundOnElements(iframeDocument);
            }
            catch (e) {
                tryFallbackScript(iframe);
            }
        }
        catch (error) {
        }
    }, [injectStyleElement, setBackgroundOnElements, tryFallbackScript]);
    const handleIframeLoad = useCallback(() => {
        setIsIframeLoaded(true);
        setIframeError(false);
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
            const attempts = [0, 100, 300, 500, 1000, 2000];
            attempts.forEach(delay => {
                setTimeout(() => {
                    applyBackgroundToIframe(iframe);
                }, delay);
            });
            try {
                setTimeout(() => {
                    try {
                        let iframeDocument = null;
                        try {
                            iframeDocument = iframe.contentDocument || (iframe.contentWindow?.document ?? null);
                        }
                        catch (e) {
                            return;
                        }
                        if (iframeDocument?.body) {
                            const observer = new MutationObserver(() => {
                                applyBackgroundToIframe(iframe);
                            });
                            observer.observe(iframeDocument.body, {
                                childList: true,
                                subtree: true,
                                attributes: true,
                                attributeFilter: ['style', 'class'],
                            });
                            if (iframeDocument.documentElement) {
                                observer.observe(iframeDocument.documentElement, {
                                    childList: true,
                                    subtree: true,
                                    attributes: true,
                                    attributeFilter: ['style', 'class'],
                                });
                            }
                        }
                    }
                    catch (e) {
                    }
                }, 500);
            }
            catch (e) {
            }
        }
    }, [applyBackgroundToIframe]);
    const handleIframeError = useCallback(() => {
        setIframeError(true);
        setIsIframeLoaded(false);
    }, []);
    return (_jsxs("div", { className: `${styles.termsOfUseContentWrapper} terms-of-use`, style: {
            minHeight: '100%',
            backgroundColor: '#FAFAFA',
            background: '#FAFAFA',
            backgroundImage: 'none',
        }, "data-testid": 'terms-of-use-content-wrapper', children: [_jsx("div", { style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: '#FAFAFA',
                    background: '#FAFAFA',
                    zIndex: 0,
                    pointerEvents: 'none',
                }, "aria-hidden": 'true' }), !isIframeLoaded && !iframeError && (_jsx("div", { className: styles.loadingContainer, "data-testid": 'terms-of-use-loading-container', children: _jsx("div", { className: styles.loadingText, "data-testid": 'terms-of-use-loading-text', children: "Carregando..." }) })), iframeError && (_jsx("div", { className: styles.errorContainer, "data-testid": 'terms-of-use-error-container', children: _jsx("div", { className: styles.errorText, "data-testid": 'terms-of-use-error-text', children: "Erro ao carregar os termos de uso." }) })), _jsx("iframe", { ref: iframeRef, src: sourceUri, className: styles.termsIframe, onLoad: handleIframeLoad, onError: handleIframeError, title: 'Termos de Uso', sandbox: 'allow-scripts allow-same-origin allow-popups', referrerPolicy: 'no-referrer-when-downgrade', style: {
                    display: isIframeLoaded ? 'block' : 'none',
                    backgroundColor: '#FAFAFA',
                    background: '#FAFAFA',
                }, "data-testid": 'terms-of-use-iframe' })] }));
}
